import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { SYSTEM_CODE, systemCodeToVerifyBeforeEntry } from '@constants/flight.constant';
import { CustomerConfigService } from '@core/configs/customer-config.service';
import { CustomerInfoResolver, VerifyBookingReq } from '@models/booking.model';
import { BaseResponse } from '@models/common.model';
import { Flight, FlightFare, FlightResultSelected, SegmentModel } from '@models/flight.model';
import { StoreModel } from '@models/store.model';
import { Store } from '@ngrx/store';
import { BookingService } from '@services/booking.service';
import { DropdownListService } from '@services/dropdown-list.service';
import { FlightService } from '@services/flight.service';
import {
    updateFareChange,
    updateSearchParams,
    updateSegments,
    updateSelectedFlights,
    updateVerifyLoading,
} from '@store/actions/flight.action';
import { selectFormParam, selectSelectedFlights } from '@store/selectors/flight.selector';
import FlightUtils from '@utils/flight.util';
import { convertStringToNumber } from '@utils/number.util';
import moment from 'moment';
import { catchError, finalize, of, switchMap } from 'rxjs';

/**
 * Verify local value
 *
 * @param {(FlightResultSelected | null)} data
 * @return {*}  {boolean}
 */
const verifyLocalValue = (data: FlightResultSelected | null): boolean => {
    if (!data) {
        return false;
    }

    const hasValidFlights = data.selectedFlights && data.selectedFlights.length > 0;
    const hasValidItinerary = data.itinerary && data.itinerary > 0 && data.itinerary <= 3;
    const hasValidSeat = !!data.seat;
    const [adult, child, infant] = data.passenger
        .split('.')
        .map((p) => convertStringToNumber(p, 0));
    const hasValidPassenger =
        data.passenger && FlightUtils.passengerIsValid({ adult, child, infant });

    return hasValidFlights && !!hasValidItinerary && hasValidSeat && !!hasValidPassenger;
};

export const customerInfoResolver: ResolveFn<CustomerInfoResolver | null> = (route) => {
    const store: Store<StoreModel> = inject(Store);
    const flightService = inject(FlightService);
    const bookingService = inject(BookingService);
    const dropdownService = inject(DropdownListService);
    const customerConfig = inject(CustomerConfigService);
    const selectedFlights = store.selectSignal(selectSelectedFlights);
    const searchParams = store.selectSignal(selectFormParam);
    const id = route.queryParamMap.get('id');
    let data = new CustomerInfoResolver();

    const hasSelectedFlights =
        selectedFlights().length > 0 &&
        selectedFlights().every((s) => s.FlightFare?.FarePricings.length);

    if (!id) {
        return null;
    }

    const findLocal = flightService.getSelectedFromLocalStorage(id);
    if (findLocal && !hasSelectedFlights) {
        if (!verifyLocalValue(findLocal)) {
            return null;
        }

        store.dispatch(updateSelectedFlights({ selectedFlights: findLocal.selectedFlights }));
        const segments: SegmentModel[] = findLocal.selectedFlights.map((f) => ({
            Origin: f.Origin,
            Destination: f.Destination,
            FlightDate: moment(f.DepartureDate).format('YYYY-MM-DD'),
        }));
        store.dispatch(updateSegments({ segments: segments }));
        store.dispatch(
            updateSearchParams({
                itinerary: findLocal.itinerary,
                seatClass: findLocal.seat,
                passengers: findLocal.passenger,
            })
        );
        data = new CustomerInfoResolver(findLocal);
    } else {
        data = new CustomerInfoResolver({
            selectedFlights: selectedFlights(),
            itinerary: searchParams().itinerary,
            seat: searchParams().seatClass,
            passenger: searchParams().passenger,
        });
    }

    // verify before enter page
    const verify = (code: string) => systemCodeToVerifyBeforeEntry.includes(code as SYSTEM_CODE);
    const allowVerify = data.selectedFlights.some((flight) => verify(flight.SystemCode as string));

    const [first] = data.selectedFlights;
    const isCombine = data.selectedFlights.some(
        (f) => !!f.CombineFlightId && !!f.CombineFlightFare
    );
    const reqBody = new VerifyBookingReq({
        Flights: data.selectedFlights as Flight[],
        ...(isCombine && { TotalFareAmount: first.TotalFareAmount, IsCombine: true }),
    });
    const ids = data.selectedFlights.map((f) => f.Id).join('|');
    store.dispatch(updateVerifyLoading({ loading: ids }));

    return dropdownService.getAirportMap().pipe(
        switchMap((group) => {
            customerConfig.setAirportGroups(group);
            const segments: SegmentModel[] = data.selectedFlights.map((f) => {
                const origin = group[f.Origin];
                const destination = group[f.Destination];
                return {
                    Origin: f.Origin,
                    Destination: f.Destination,
                    FlightDate: f.DepartureDate as string,
                    CountryCodeOrigin: origin?.RegionCode,
                    CountryCodeDestination: destination?.RegionCode,
                    GeoCountryCodeOrigin: origin?.GeoCountryCode,
                    GeoCountryCodeDestination: destination?.GeoCountryCode,
                    IsInternational: origin?.International || destination?.International,
                };
            });

            const [adult, child, infant] = data.passenger
                .split('.')
                .map((p) => convertStringToNumber(p, 0));

            reqBody.ServiceFee = {
                Adult: adult,
                Child: child,
                Infant: infant,
                Cabin: data.seat,
                Segments: segments,
                Itinerary: data.itinerary,
                PromoCode: '',
            };
            data.request = reqBody;

            if (!allowVerify) {
                return of(data);
            }

            return bookingService.verifyBooking(reqBody).pipe(
                catchError(() => of(new BaseResponse({ Data: null }))),
                switchMap((res) => {
                    if (res.ID !== 1 || !res.Data) {
                        return of({
                            ...data,
                            verifyFailed: true,
                            verifyMessage: res?.Message,
                        });
                    }

                    const { Data } = res;
                    const verifyData = Data.filter((d) => verify(d.SystemCode) && d.ID !== -1);
                    data.verifySessions = new Map(
                        verifyData.map((v) => [v.SystemCode, v.SessionVerify])
                    );
                    data.verifyFailed = Data.some((d) => d.ID === -1 && verify(d.SystemCode));
                    const fareChange = verifyData.find((d) => d.ID === 0);
                    data.fareChanged = !!fareChange;

                    if (data.fareChanged) {
                        data.verifyMessage = fareChange?.Message;
                        store.dispatch(
                            updateFareChange({
                                fare: verifyData
                                    .filter((v) => v.VerifyData?.FareInfo)
                                    .map((v) => ({
                                        ...(v.VerifyData?.FareInfo as FlightFare),
                                        SystemCode: v.SystemCode,
                                    })),
                            })
                        );
                    }
                    return of(data);
                }),
                finalize(() => store.dispatch(updateVerifyLoading({ loading: null })))
            );
        })
    );
};
